import React from "react";
import { Link } from "react-router-dom";
import { FiArrowDown, FiArrowUpRight, FiCheck, FiMapPin } from "react-icons/fi";

const strengths = [
  "Business-first thinking",
  "End-to-end digital expertise",
  "Collaborative by default",
];

const AboutHome = () => {
  return (
    <section className="relative isolate overflow-hidden bg-[#070a10] px-5 pb-16 pt-28 text-white md:px-8 md:pb-20 md:pt-32 lg:px-12">
      <div className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_15%_20%,rgba(255,219,22,0.10),transparent_30%),radial-gradient(circle_at_85%_15%,rgba(255,219,22,0.07),transparent_25%),radial-gradient(circle_at_60%_90%,rgba(255,219,22,0.05),transparent_35%)]" />
      <div className="absolute inset-0 -z-20 opacity-[0.03] [background-image:linear-gradient(rgba(255,255,255,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.35)_1px,transparent_1px)] [background-size:80px_80px] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]" />
      <div className="absolute right-[4%] top-24 -z-10 h-20 w-20 rotate-12 rounded-[24px] border border-white/10 bg-white/[0.025]" />

      <div className="mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12 xl:gap-16">
        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 backdrop-blur-xl">
            <span className="h-2 w-2 rounded-full bg-[#FFDB16] shadow-[0_0_16px_rgba(255,219,22,0.55)]" />
            <span className="text-xs font-bold tracking-wide text-slate-200">
              About Abscod Informatics
            </span>
          </div>

          <h1 className="max-w-2xl font-heading text-[2rem] font-extrabold leading-[1.08] tracking-[-0.035em] text-white sm:text-[2.75rem] lg:text-[3.25rem] xl:text-[3.75rem]">
            Technology built around
            <span className="relative mt-1 block w-fit text-[#FFDB16]">
              your ambition.
              <span className="absolute -bottom-1.5 left-0 h-1 w-20 rounded-full bg-[#FFDB16] sm:w-28" />
            </span>
          </h1>

          <p className="mt-5 max-w-lg text-base leading-7 text-slate-300 md:text-lg lg:text-base lg:leading-7 xl:text-lg xl:leading-8">
            We bring strategy, design, and engineering together to turn complex
            business challenges into digital experiences that feel simple,
            useful, and built to last.
          </p>

          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {strengths.map((strength, index) => (
              <div
                key={strength}
                className={index === strengths.length - 1 ? "sm:col-span-2 lg:col-span-1 xl:col-span-2" : ""}
              >
                <span className="flex items-center gap-2 text-xs font-semibold text-slate-200">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFDB16]/15 text-[#FFDB16]">
                    <FiCheck className="h-3.5 w-3.5" />
                  </span>
                  {strength}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center">
            <Link
              to="/contactus"
              className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[#FFDB16] px-5 py-2 text-sm font-bold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_40px_rgba(255,219,22,0.2)]"
            >
              Start a conversation
              <FiArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#who-we-are"
              className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-5 py-2 text-sm font-bold text-white backdrop-blur-xl transition duration-300 hover:border-white/30 hover:bg-white/[0.1]"
            >
              Discover our story
              <FiArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-[610px] lg:mx-0">
          <div className="absolute -inset-4 -z-10 rounded-[40px] bg-gradient-to-br from-[#FFDB16]/10 via-transparent to-[#FFDB16]/5 blur-2xl" />
          <div className="relative overflow-hidden rounded-[30px] border border-white/15 bg-white/[0.06] p-2 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-3">
            <img
              src="/Images/about1.png"
              alt="Abscod Informatics office workspace"
              className="h-[280px] w-full rounded-[23px] object-cover object-center sm:h-[340px] lg:h-[380px] xl:h-[420px]"
            />
            <div className="pointer-events-none absolute inset-3 rounded-[23px] bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />

            <div className="absolute inset-x-7 bottom-7 flex items-end justify-between gap-4 sm:inset-x-9 sm:bottom-9">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#FFDB16]">
                  Where ideas take shape
                </p>
                <p className="mt-1.5 max-w-xs font-heading text-base font-extrabold leading-tight text-white sm:text-lg">
                  A team that thinks with you, not just for you.
                </p>
              </div>
              <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FFDB16] text-slate-950 sm:flex">
                <FiArrowUpRight className="h-5 w-5" />
              </span>
            </div>
          </div>

          <div className="absolute -left-5 top-9 hidden items-center gap-2 rounded-2xl border border-white/70 bg-white px-3 py-2 text-slate-950 shadow-xl sm:flex lg:-left-8">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFDB16] text-slate-950">
              <FiMapPin className="h-4 w-4" />
            </span>
            <span>
              <span className="block text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
                Our studio
              </span>
              <span className="mt-0.5 block text-xs font-bold">Ghaziabad, India</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHome;
